import { ISaSmsTenantDetail, SaSmsFeatures } from '@interfaces/sa-sms';
import { Card, Descriptions, Form, Input, notification, Switch, Tag } from 'antd';
import { useSaSmsWrite } from 'hooks/use-sa-sms-api';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SaSmsReasonModal } from './sa-sms-reason-modal';

type Pending = {
    field: 'marketingEnabled' | 'twoWayEnabled' | 'voiceEnabled' | 'outboundPaused';
    value: boolean;
};

type Props = {
    detail: ISaSmsTenantDetail;
    features?: SaSmsFeatures;
    canEdit: boolean;
    onChanged: () => void;
};

/** Marketing / Two-Way / Voice switches and the outbound pause. Each change asks for a reason (audited). */
export const SmsTenantToggles: React.FC<Props> = ({ detail, features, canEdit, onChanged }) => {
    const { t } = useTranslation(['sa-sms']);
    const { send } = useSaSmsWrite();
    const [pending, setPending] = useState<Pending | null>(null);
    const cfg = detail.config.config;

    const rows: Array<{ field: Pending['field']; on: boolean }> = [
        { field: 'marketingEnabled', on: cfg.marketing_enabled !== false },
        { field: 'twoWayEnabled', on: cfg.two_way_enabled !== false },
        ...(features?.voice
            ? [{ field: 'voiceEnabled' as const, on: cfg.voice_enabled !== false }]
            : []),
    ];

    const confirm = async (values: { reason: string; pausedReason?: string }) => {
        if (!pending) return;
        const body: Record<string, unknown> = {
            [pending.field]: pending.value,
            reason: values.reason,
        };
        if (pending.field === 'outboundPaused' && pending.value)
            body.pausedReason = values.pausedReason?.trim() || 'admin';
        await send('put', `/tenants/${detail.tenantId}/config`, body);
        notification.success({ message: t('saved') });
        setPending(null);
        onChanged();
    };

    return (
        <Card title={t('detail.toggles')}>
            <Descriptions column={1} size="small" bordered>
                {rows.map((r) => (
                    <Descriptions.Item key={r.field} label={t(`toggles.${r.field}`)}>
                        <Switch
                            checked={r.on}
                            disabled={!canEdit}
                            onChange={(value) => setPending({ field: r.field, value })}
                        />
                    </Descriptions.Item>
                ))}
                <Descriptions.Item label={t('toggles.outboundPaused')}>
                    <Switch
                        checked={cfg.outbound_paused}
                        disabled={!canEdit}
                        onChange={(value) => setPending({ field: 'outboundPaused', value })}
                    />
                    {cfg.outbound_paused && cfg.paused_reason && (
                        <Tag color="error" style={{ marginLeft: 8 }}>
                            {t(`pausedReason.${cfg.paused_reason}`, {
                                defaultValue: cfg.paused_reason,
                            })}
                        </Tag>
                    )}
                </Descriptions.Item>
            </Descriptions>
            <SaSmsReasonModal<{ pausedReason?: string }>
                open={!!pending}
                danger={pending?.field === 'outboundPaused' && pending.value}
                title={
                    pending
                        ? t(`toggles.confirm.${pending.field}.${pending.value ? 'on' : 'off'}`)
                        : ''
                }
                warning={
                    pending?.field === 'outboundPaused' && pending.value
                        ? t('toggles.pauseWarning')
                        : undefined
                }
                onCancel={() => setPending(null)}
                onConfirm={confirm}
            >
                {pending?.field === 'outboundPaused' && pending.value && (
                    <Form.Item
                        name="pausedReason"
                        label={t('toggles.pausedReasonLabel')}
                        rules={[{ max: 48 }]}
                    >
                        <Input maxLength={48} placeholder="abuse_review" />
                    </Form.Item>
                )}
            </SaSmsReasonModal>
        </Card>
    );
};
